import React, { useRef } from 'react';
import { type DateRange, type Matcher } from 'react-day-picker';

import { useDateFormatter } from '@mint-lib/formatting-hooks';
import { useDateLocalize } from '@mint-lib/formatting-hooks/locales';
import { addYears, endOfYear, format } from 'date-fns';

import { useTranslation } from '../../../../i18n/src/i18n.js';
import { useMobile } from '../../hooks/useMobile.js';
import { Button } from '../../lib/button.jsx';
import { Calendar } from '../../lib/calendar.jsx';
import { Input } from '../../lib/input.jsx';
import {
  DropdownNavigationWrapper,
  NextMonthButton,
  PreviousMonthButton,
  SelectNavigation,
} from './Navigation.jsx';

type CalendarRangeProps = {
  /**
   * Object with selected dates: from and to
   */
  selected: DateRange;
  /**
   * Callback for selecting a date range. It will be called when the date is selected.
   */
  onSelect: (dateRange?: DateRange) => void;
  /**
   * The initial month to be displayed in the calendar.
   */
  defaultMonth?: Date;
  /**
   * The month to be displayed in the calendar.
   */
  month?: Date;
  /**
   * Callback for selecting a month. It will be called when the month is selected.
   */
  onMonthChange?: (date: Date) => void;
  /* Callback that applies the selected date range. It will be called when the `Apply` button is clicked.
   */
  onApply: (dateRange?: DateRange) => void;
  /**
   * Callback that resets the selected date range. It will be called when the `Reset` button is clicked.
   */
  onReset?: (dateRange?: DateRange) => void;
  /**
   * Helps to disable dates in various ways
   */
  disabledDates?: Matcher | Matcher[];
  /**
   * Event dates to be highlighted in the calendar
   */
  eventDates?: Date[];
};
const CalendarRange = (props: CalendarRangeProps) => {
  const calendarRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('@mint-lib/tw-components');
  const localize = useDateLocalize();
  const isMobile = useMobile();
  const { formattedDateFrom, formattedDateTo } = useDateFormatter(
    props.selected?.from,
    props.selected?.to,
  );

  // handler for applying the selected date
  const handleDateApply = () => {
    props.onApply?.({
      from: props.selected?.from,
      to: props.selected?.to,
    });
  };
  // handler for resetting the selected date inside the calendar
  const handleReset = () => {
    props.onSelect?.({ from: undefined, to: undefined });
    if (props.onReset) {
      props.onReset({ from: undefined, to: undefined });
    } else {
      props.onApply?.({ from: undefined, to: undefined });
    }
  };

  return (
    <div ref={calendarRef}>
      <Calendar
        {...props}
        data-testid="calendar-range"
        mode="range"
        showOutsideDays={false}
        selected={props.selected}
        onSelect={(date, _triggerDate, _modifiers, e) => {
          props.onSelect?.(date);
        }}
        month={props.month}
        onMonthChange={props.onMonthChange}
        numberOfMonths={isMobile ? 1 : 2}
        locale={localize}
        endMonth={endOfYear(addYears(new Date(), 50))}
        captionLayout="dropdown"
        components={{
          Dropdown: (selectProps) => {
            return (
              <SelectNavigation
                {...selectProps}
                selectSize={isMobile ? 'medium' : 'small'}
                container={calendarRef.current}
              />
            );
          },
          DropdownNav: DropdownNavigationWrapper,
          NextMonthButton: NextMonthButton,
          PreviousMonthButton: PreviousMonthButton,
        }}
        formatters={{
          formatWeekdayName: (date) =>
            format(date, 'ccccc', {
              locale: localize,
            }),
        }}
        disabled={props.disabledDates}
        modifiers={{
          eventDays: props.eventDates,
        }}
        footer={
          <div className="flex w-80 flex-col place-content-between gap-10 border-t border-border-secondary py-2 md:w-full md:flex-row md:gap-0 md:px-3">
            <div className="flex w-full items-center justify-center gap-2 md:justify-start">
              <Input
                size={isMobile ? 'medium' : 'small'}
                placeholder={t('Start date')}
                readOnly
                className="w-37 md:w-28"
                value={formattedDateFrom}
              />
              {isMobile ? null : <p className="text-text-primary">-</p>}
              <Input
                size={isMobile ? 'medium' : 'small'}
                placeholder={t('End date')}
                readOnly
                className="w-37 md:w-28"
                value={formattedDateTo}
              />
            </div>
            <div className="flex justify-end gap-2 px-2 md:items-center md:px-0">
              <Button
                variant="text"
                size={isMobile ? 'large' : 'medium'}
                onClick={handleReset}
              >
                {t('Reset')}
              </Button>
              <Button
                color="primary"
                size={isMobile ? 'large' : 'medium'}
                onClick={handleDateApply}
                disabled={!formattedDateFrom}
              >
                {t('Apply')}
              </Button>
            </div>
          </div>
        }
      />
    </div>
  );
};
export default CalendarRange;
